import { useState, useEffect, useCallback, useMemo } from "react";
import { Lock, Plus, Copy, Check, Search, RefreshCw } from "lucide-react";
import SecureLinkModal from "../components/SecureLinkModal";
import { getSecureLinks, revokeSecureLink } from "../api";

// Send Secure Data — send a password, file or other sensitive information to
// anyone without opening a ticket first. Composing reuses SecureLinkModal (the
// same form the ticket page uses); this page adds the history and revoke.

const panel = { background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 10 };
const th = { textAlign: "left", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--t-text-label)", padding: "10px 12px", borderBottom: "1px solid var(--t-border-soft)", whiteSpace: "nowrap" };
const td = { padding: "10px 12px", fontSize: 13, color: "var(--t-text-body)", borderBottom: "1px solid var(--t-border-soft)", verticalAlign: "middle" };
const pill = (c) => ({ display: "inline-block", fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 999, background: c + "22", color: c, border: `1px solid ${c}55` });
const btnSm = { display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 9px", fontSize: 12, borderRadius: 6, border: "1px solid var(--t-border-soft)", background: "transparent", color: "var(--t-text-body)", cursor: "pointer" };

const statusOf = (l) => l.revoked ? ["Revoked", "#ef4444"]
  : new Date(l.expires_at) < new Date() ? ["Expired", "#64748b"]
  : (l.max_views > 0 && l.view_count >= l.max_views) ? ["Used up", "#f59e0b"]
  : ["Active", "#22c55e"];

const fmt = (iso) => iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

export default function SendSecureData() {
  const [links,   setLinks]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [q,       setQ]       = useState("");
  const [filter,  setFilter]  = useState("active");
  const [copied,  setCopied]  = useState(null);
  const [busy,    setBusy]    = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await getSecureLinks(); setLinks(r.data || []); }
    catch (e) { setError(e?.response?.data?.error || "Could not load secure links."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const activeCount = links.filter(l => statusOf(l)[0] === "Active").length;
  const shown = useMemo(() => links.filter(l => {
    if (filter === "active" && statusOf(l)[0] !== "Active") return false;
    const s = q.trim().toLowerCase();
    return !s || [l.title, l.recipient_email, l.created_by_name].some(v => (v || "").toLowerCase().includes(s));
  }), [links, q, filter]);

  const copy = (l) => {
    navigator.clipboard.writeText(`${window.location.origin}/share/${l.token}`);
    setCopied(l.token);
    setTimeout(() => setCopied(null), 1800);
  };

  const revoke = async (l) => {
    if (!window.confirm(`Revoke "${l.title || "Untitled"}" sent to ${l.recipient_email || "—"}? The link stops working immediately.`)) return;
    setBusy(l.token);
    try { await revokeSecureLink(l.token); await load(); }
    catch { setError("Could not revoke the link."); }
    finally { setBusy(null); }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      {showNew && <SecureLinkModal onClose={() => { setShowNew(false); load(); }} />}

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 18 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "var(--t-text-primary)", letterSpacing: "-0.5px", display: "flex", alignItems: "center", gap: 10 }}>
            <Lock size={20} color="#3b82f6" /> Send Secure Data
          </h1>
          <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--t-text-label)", maxWidth: 680, lineHeight: 1.6 }}>
            Send a password, file or other sensitive information to anyone — no ticket needed. They get an email with a
            secure link; to open it we email them a one-time code, so no account is required. The content itself is
            never put in the email.
          </p>
        </div>
        <button onClick={() => setShowNew(true)}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 18px", background: "linear-gradient(135deg,#1d4ed8,#3b82f6)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          <Plus size={14} /> New secure link
        </button>
      </div>

      <div style={{ ...panel, padding: 16 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
          <div style={{ position: "relative", flex: "1 1 260px" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: 10, color: "var(--t-text-label)" }} />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by title, recipient or sender…"
              style={{ width: "100%", background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 8, color: "var(--t-text-body)", padding: "8px 11px 8px 30px", fontSize: 13, boxSizing: "border-box" }} />
          </div>
          {[["active", `Active (${activeCount})`], ["all", `All (${links.length})`]].map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)}
              style={{ ...btnSm, padding: "7px 12px", background: filter === k ? "rgba(59,130,246,0.15)" : "transparent", borderColor: filter === k ? "rgba(59,130,246,0.5)" : "var(--t-border-soft)", color: filter === k ? "#93c5fd" : "var(--t-text-body)" }}>
              {label}
            </button>
          ))}
          <button onClick={load} title="Refresh" style={btnSm}><RefreshCw size={13} /></button>
        </div>

        {error && <div style={{ background: "#450a0a", border: "1px solid #7f1d1d", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#fca5a5", marginBottom: 12 }}>{error}</div>}

        {loading ? (
          <div style={{ padding: 30, textAlign: "center", color: "var(--t-text-label)", fontSize: 13 }}>Loading…</div>
        ) : shown.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "var(--t-text-label)", fontSize: 13 }}>
            {links.length ? (filter === "active" ? "No active links. Switch to All to see past ones." : "No links match.") : "No secure links sent yet. Use “New secure link” to send one."}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
              <thead><tr>{["Title", "Recipient", "Sent by", "Sent", "Expires", "Views", "Status", ""].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {shown.map(l => {
                  const [st, clr] = statusOf(l);
                  const live = st === "Active";
                  return (
                    <tr key={l.token}>
                      <td style={{ ...td, fontWeight: 600, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={l.title || ""}>
                        {l.title || <em style={{ color: "var(--t-text-faint)" }}>Untitled</em>}
                      </td>
                      <td style={{ ...td, fontSize: 12, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={l.recipient_email || ""}>{l.recipient_email || "—"}</td>
                      <td style={{ ...td, fontSize: 12 }}>{l.created_by_name || "—"}</td>
                      <td style={{ ...td, fontSize: 12, whiteSpace: "nowrap" }}>{fmt(l.created_at)}</td>
                      <td style={{ ...td, fontSize: 12, whiteSpace: "nowrap" }}>{fmt(l.expires_at)}</td>
                      <td style={{ ...td, fontSize: 12 }}>{l.view_count} / {l.max_views === 0 ? "∞" : l.max_views}</td>
                      <td style={td}><span style={pill(clr)}>{st}</span></td>
                      <td style={{ ...td, whiteSpace: "nowrap", textAlign: "right" }}>
                        {live && (
                          <button style={btnSm} onClick={() => copy(l)}>
                            {copied === l.token ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                          </button>
                        )}{" "}
                        {!l.revoked && st !== "Expired" && (
                          <button style={{ ...btnSm, color: "#fca5a5", borderColor: "rgba(239,68,68,0.4)" }} disabled={busy === l.token} onClick={() => revoke(l)}>
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
